import {
  ArrowsClockwiseIcon,
  HeadsetIcon,
  SlidersHorizontalIcon,
  StarIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';

import { ActionMenu, type ActionMenuItem } from '@/components/common/action-menu';

import { REPLACEMENT_WINDOW_DAYS, isFinalStatus } from '../constants';
import { useOrderStatusActions } from '../order-actions';
import type { OrderDetail } from '../types';

function withinReplacementWindow(deliveredAt: string | null): boolean {
  if (!deliveredAt) return false;
  return dayjs().diff(dayjs(deliveredAt), 'day') <= REPLACEMENT_WINDOW_DAYS;
}

type Props = {
  order: OrderDetail;
  onRaiseTicket: () => void;
  onReadReview: () => void;
  onRequestReplacement: () => void;
  onAddParameters: (lineItemId: string | null) => void;
};

/** Kebab menu; items composed by status, review availability, and replacement window. */
export function OrderActionsMenu({
  order,
  onRaiseTicket,
  onReadReview,
  onRequestReplacement,
  onAddParameters,
}: Props) {
  const { cancelOrder } = useOrderStatusActions(order);
  const delivered = order.status === 'delivered';
  const hasUnconfigured = order.lineItems.some(
    (li) => li.parameterApplicable && !li.parameterConfigured,
  );

  const items: ActionMenuItem[] = [
    {
      key: 'ticket',
      label: 'Raise a Ticket',
      icon: <HeadsetIcon size={18} />,
      onClick: onRaiseTicket,
    },
  ];
  if (delivered && order.hasReview) {
    items.push({
      key: 'review',
      label: 'Read Review',
      icon: <StarIcon size={18} />,
      onClick: onReadReview,
    });
  }
  if (delivered && withinReplacementWindow(order.deliveredAt)) {
    items.push({
      key: 'replacement',
      label: 'Request Replacement',
      icon: <ArrowsClockwiseIcon size={18} />,
      onClick: onRequestReplacement,
    });
  }
  if (hasUnconfigured) {
    items.push({
      key: 'params',
      label: 'Add Parameter Template',
      icon: <SlidersHorizontalIcon size={18} />,
      onClick: () => onAddParameters(null),
    });
  }
  if (!isFinalStatus(order.status)) {
    items.push({
      key: 'cancel',
      label: 'Cancel Order',
      icon: <XCircleIcon size={18} />,
      danger: true,
      onClick: () => void cancelOrder(),
    });
  }

  return <ActionMenu triggerLabel="Order actions" groups={[{ items }]} />;
}
